<template>
  <div
    :class="`chat-contact d-flex align-center pa-3 cursor-pointer rounded-lg ${ticketEstaSendoVisualizado ? 'chat-ativo white--text active-chat-contact' : 'chat-inativo'}`"
    @click="handleClick"
  >
    <div class="d-flex flex-column">
      <div
        v-if="slaCriticoTipoAlertaId"
        class="mb-1"
      >
        <sla-badge-azul
          v-if="isSlaCriticoTipoAlertaAzul"
          show-tooltip
          :tooltip-text="slaCriticoTooltipText.first"
          :tooltip-text2="slaCriticoTooltipText.second"
        />

        <sla-badge-verde
          v-else-if="isSlaCriticoTipoAlertaVerde"
          show-tooltip
          :tooltip-text="slaCriticoTooltipText.first"
          :tooltip-text2="slaCriticoTooltipText.second"
        />

        <sla-badge-laranja
          v-else-if="isSlaCriticoTipoAlertaLaranja"
          show-tooltip
          :tooltip-text="slaCriticoTooltipText.first"
          :tooltip-text2="slaCriticoTooltipText.second"
        />

        <sla-badge-vermelho
          v-else-if="isSlaCriticoTipoAlertaVermelho"
          show-tooltip
          :tooltip-text="slaCriticoTooltipText.first"
          :tooltip-text2="slaCriticoTooltipText.second"
        />
      </div>

      <v-avatar
        size="20"
        class="me-3 user-status-badge"
      >
        <v-icon :dark="ticketEstaSendoVisualizado">
          {{ ticketCanalIcon }}
        </v-icon>
      </v-avatar>
    </div>

    <div class="flex-grow-1 overflow-hidden">
      <p :class="`${ticketEstaSendoVisualizado ? 'white--text' : 'text-primary'} mb-0 font-weight-medium`">
        {{ ticketData.id }} {{ ticketData.solicitanteNome }}
      </p>
      <p :class="`${ticketEstaSendoVisualizado ? 'white--text' : 'text--secondary'} mb-0 text-truncate`">
        {{ ticketData.ultimaMensagemTicket || ticketData.titulo }}
      </p>
    </div>

    <div class="chat-meta d-flex flex-column align-self-stretch align-end">
      <p class="text-no-wrap mb-0">
        {{ formatDateToMonthShort(ticketData.dataUltimaMensagem) }}
      </p>

      <v-badge
        v-if="ticketData.qtdMensagensNaoRespondidas"
        color="error"
        inline
        class="number-badge"
        :content="ticketData.qtdMensagensNaoRespondidas"
      />
    </div>

    <ticket-sair-dialog
      :mudar-de-ticket.sync="mudarDeTicket"
      @sair-e-selecionar-ticket="handleSairESelecionarTicket"
    />
  </div>
</template>

<style lang="scss">
.chat-ativo {
  background: linear-gradient(98deg, #d4d4d468, var(--v-primary-base) 0%);
}
.chat-inativo:hover {
  background-color: rgba(196, 142, 255, 0.2);
}
</style>

<script>
import { ref, computed, watchEffect } from '@vue/composition-api'
import { mdiFileEditOutline, mdiGestureTapButton, mdiEmailOutline, mdiWhatsapp, mdiFacebookMessenger } from '@mdi/js'
import { storeToRefs } from 'pinia'
import { formatDateToMonthShort } from '@core/utils/filter'
import TicketSairDialog from './TicketSairDialog.vue'
import SlaBadgeAzul from '@/components/sla-badge/SlaBadgeAzul.vue'
import SlaBadgeVerde from '@/components/sla-badge/SlaBadgeVerde.vue'
import SlaBadgeLaranja from '@/components/sla-badge/SlaBadgeLaranja.vue'
import SlaBadgeVermelho from '@/components/sla-badge/SlaBadgeVermelho.vue'
import TiposDeCanaisEnum from '@/models/entities/canal/TiposDeCanaisEnum'
import ChatBotProvedorComunicacaoEnum from '@/models/entities/canal/ChatBotProvedorComunicacaoEnum'
import SlaTipoEnum from '@/models/entities/sla/SlaTipoEnum'
import SlaTipoAlertaEnum from '@/models/entities/sla/SlaTipoAlertaEnum'
import useAtendimentoStore from '@/store/atendimento'
import { formatarMinutos } from '@/utils/time'

export default {
  components: {
    TicketSairDialog,
    SlaBadgeAzul,
    SlaBadgeVerde,
    SlaBadgeLaranja,
    SlaBadgeVermelho,
  },

  props: {
    ticketData: {
      type: Object,
      required: true,
    },
  },

  setup(props) {
    const store = useAtendimentoStore()
    const { setTicketSelecionadoId, setTicketSelecionadoFoiAlterado } = store
    const { ticketSelecionadoId, ticketSelecionadoFoiAlterado } = storeToRefs(store)

    const mudarDeTicket = ref(false)

    const ticketEstaSendoVisualizado = ref(false)
    watchEffect(() => { ticketEstaSendoVisualizado.value = ticketSelecionadoId.value === props.ticketData.id })

    function handleClick() {
      if (ticketSelecionadoFoiAlterado.value && !ticketEstaSendoVisualizado.value) {
        mudarDeTicket.value = true
      } else {
        setTicketSelecionadoId(props.ticketData.id)
      }
    }

    function handleSairESelecionarTicket() {
      setTicketSelecionadoId(props.ticketData.id)
      setTicketSelecionadoFoiAlterado(false)
    }

    const ticketCanalIcon = computed(() => {
      switch (props.ticketData.canalTipoId) {
        case TiposDeCanaisEnum.AberturaManual: {
          return mdiFileEditOutline
        }
        case TiposDeCanaisEnum.Autoatendimento: {
          return mdiGestureTapButton
        }
        case TiposDeCanaisEnum.Email: {
          return mdiEmailOutline
        }
        case TiposDeCanaisEnum.WhatsApp: {
          return mdiWhatsapp
        }
        case TiposDeCanaisEnum.MetaMessenger: {
          return mdiFacebookMessenger
        }
        case TiposDeCanaisEnum.Bot: {
          if (props.ticketData.chatBotProvedorComunicacaoId === ChatBotProvedorComunicacaoEnum.WhatsApp) {
            return mdiWhatsapp
          }

          if (props.ticketData.chatBotProvedorComunicacaoId === ChatBotProvedorComunicacaoEnum.MetaMessenger) {
            return mdiFacebookMessenger
          }

          return null
        }
        default:
          return null
      }
    })

    // Faixa mais crítica entre os SLAs (Primeira Resposta / Finalização) ainda não concluídos do ticket -
    // já vem calculada pelo backend junto com a linha da listagem, sem chamada/endpoint separado.
    const slaCriticoTipoId = computed(() => props.ticketData.slaTipoCriticoId)
    const slaCriticoTipoAlertaId = computed(() => props.ticketData.slaTipoAlertaCriticoId)

    const isSlaCriticoTipoAlertaAzul = computed(() => slaCriticoTipoAlertaId.value === SlaTipoAlertaEnum.Azul)
    const isSlaCriticoTipoAlertaVerde = computed(() => slaCriticoTipoAlertaId.value === SlaTipoAlertaEnum.Verde)
    const isSlaCriticoTipoAlertaLaranja = computed(() => slaCriticoTipoAlertaId.value === SlaTipoAlertaEnum.Laranja)
    const isSlaCriticoTipoAlertaVermelho = computed(() => slaCriticoTipoAlertaId.value === SlaTipoAlertaEnum.Vermelho)

    const slaCriticoTooltipText = computed(() => {
      const nomeTipo = slaCriticoTipoId.value === SlaTipoEnum.PrimeiraRespostaTicket
        ? 'Primeira resposta'
        : 'Finalização do ticket'

      const dataLimite = props.ticketData.slaDataLimiteCritico ? new Date(props.ticketData.slaDataLimiteCritico) : null
      const diferencaMinutos = dataLimite ? Math.round((dataLimite.getTime() - Date.now()) / 60000) : null

      let tempo = ''

      if (diferencaMinutos !== null) {
        tempo = diferencaMinutos >= 0
          ? `Restam ${formatarMinutos(diferencaMinutos)}`
          : `Atrasado há ${formatarMinutos(Math.abs(diferencaMinutos))}`
      }

      return { first: `SLA de ${nomeTipo}`, second: tempo }
    })

    return {
      ticketEstaSendoVisualizado,
      mudarDeTicket,
      handleClick,
      handleSairESelecionarTicket,
      formatDateToMonthShort,
      ticketCanalIcon,

      slaCriticoTipoAlertaId,
      slaCriticoTooltipText,

      isSlaCriticoTipoAlertaAzul,
      isSlaCriticoTipoAlertaVerde,
      isSlaCriticoTipoAlertaLaranja,
      isSlaCriticoTipoAlertaVermelho,
    }
  },
}
</script>
